'use client';

import { memo, useCallback, useMemo, useState, useEffect } from 'react';

import { useParams } from 'next/navigation';
import { useTranslations } from 'next-intl';

import EnFlag from '@/assets/icons/lang/en.svg';
import FrFlag from '@/assets/icons/lang/fr.svg';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { useLanguageSync } from '@/hooks/useLanguageSync';
import { usePathname, useRouter } from '@/i18n/navigation';
import { C_LanguageNames } from '@/ts/constants/C_LanguageSync';
import { E_Locales } from '@/ts/enums/E_Locales';

const EnFlagComponent = memo(() => <EnFlag className="h-7 w-7" />);
EnFlagComponent.displayName = 'EnFlagComponent';

const FrFlagComponent = memo(() => <FrFlag className="h-7 w-7" />);
FrFlagComponent.displayName = 'FrFlagComponent';

const SmallEnFlagComponent = memo(() => <EnFlag className="h-4 w-4" />);
SmallEnFlagComponent.displayName = 'SmallEnFlagComponent';

const SmallFrFlagComponent = memo(() => <FrFlag className="h-4 w-4" />);
SmallFrFlagComponent.displayName = 'SmallFrFlagComponent';

export const LanguageSwitcher = memo(() => {
  const router = useRouter();
  const pathname = usePathname();
  const params = useParams();
  const t = useTranslations('common');
  const { setManualLanguageChange } = useLanguageSync();

  const currentLocale = params.locale as E_Locales;

  const [isOpen, setIsOpen] = useState(false);

  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(
    null
  );

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const headerEl = document.querySelector('header');
    setPortalContainer((headerEl as HTMLElement) ?? document.body);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [pathname, currentLocale]);

  const handleLanguageChange = useCallback(
    (locale: E_Locales) => {
      if (locale === currentLocale) {
        setIsOpen(false);
        return;
      }

      setManualLanguageChange(true);

      setIsOpen(false);

      router.push(pathname, { locale });
    },
    [router, pathname, setManualLanguageChange, currentLocale]
  );

  const localeFlags: Record<E_Locales, React.ReactNode> = useMemo(
    () => ({
      [E_Locales.EN]: <EnFlagComponent />,
      [E_Locales.FR]: <FrFlagComponent />
    }),
    []
  );

  const smallLocaleFlags: Record<E_Locales, React.ReactNode> = useMemo(
    () => ({
      [E_Locales.EN]: <SmallEnFlagComponent />,
      [E_Locales.FR]: <SmallFrFlagComponent />
    }),
    []
  );

  const availableLocales = useMemo(
    () => Object.values(E_Locales).filter(locale => locale !== currentLocale),
    [currentLocale]
  );

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="flex h-7 w-7 items-center justify-center rounded-full p-0"
          aria-label={t('language')}
        >
          {localeFlags[currentLocale]}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        container={portalContainer}
        className="min-w-32 bg-[var(--accordion-brand)] py-2.5"
      >
        {availableLocales.map(locale => (
          <DropdownMenuItem
            key={locale}
            onClick={() => handleLanguageChange(locale)}
            className="flex w-full justify-center items-center gap-2 cursor-pointer px-5"
          >
            <span className="flex items-center justify-center h-4 w-4">
              {smallLocaleFlags[locale]}
            </span>
            <span className="uppercase flex items-center font-bold">
              {C_LanguageNames[locale]}
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
});

LanguageSwitcher.displayName = 'LanguageSwitcher';
